// @src/pre-deployment/src/data/UserGrammarTracking.js v1.0
// - User grammar access tracking system
// - Tracks which grammar points user has seen/accessed
// - Simple boolean tracking: gmr_001: true/false
// - Used for exercise prerequisites filtering

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

import { db } from '../services/firebase.js';

// User Grammar Access Tracking Service
export const UserGrammarTracking = {
  
  // Get user's grammar access status for a chapter
  async getUserGrammarGenome(userId, level, chapter, sessionType = 'Learn') {
    try {
      console.group(`🧬 UserGrammarTracking: Getting grammar genome for ${userId}`);
      console.log('Level:', level, 'Chapter:', chapter, 'Session:', sessionType);
      
      const genomePath = `users/${userId}/level_${level}/chapter_${chapter}/${sessionType}`;
      const genomeRef = doc(db, genomePath, 'grammar_genome');
      const genomeSnap = await getDoc(genomeRef);
      
      if (genomeSnap.exists()) {
        const genome = genomeSnap.data();
        console.log('✅ Grammar genome found:', genome);
        console.groupEnd();
        return genome;
      } else {
        console.log('📝 No grammar genome found, returning empty object');
        console.groupEnd();
        return {};
      }
    } catch (error) {
      console.error('❌ Error getting user grammar genome:', error);
      console.groupEnd();
      return {};
    }
  },

  // Mark grammar point as accessed/seen by user
  async markGrammarPointAccessed(userId, level, chapter, grammarId, sessionType = 'Learn') {
    try {
      console.group(`🧬 UserGrammarTracking: Marking grammar point accessed`);
      console.log('User:', userId, 'Level:', level, 'Chapter:', chapter);
      console.log('Grammar ID:', grammarId, 'Session:', sessionType);
      
      const genomePath = `users/${userId}/level_${level}/chapter_${chapter}/${sessionType}`;
      const genomeRef = doc(db, genomePath, 'grammar_genome');
      
      // Get current genome
      const genomeSnap = await getDoc(genomeRef);
      let currentGenome = genomeSnap.exists() ? genomeSnap.data() : {};
      
      // Mark grammar point as accessed
      currentGenome[grammarId] = true;
      currentGenome.last_updated = serverTimestamp();
      currentGenome.updated_by = 'user_access';
      
      // Save updated genome
      await setDoc(genomeRef, currentGenome, { merge: true });
      
      console.log(`✅ Grammar point ${grammarId} marked as accessed`);
      console.log('Updated genome:', currentGenome);
      console.groupEnd();
      
      return currentGenome;
    } catch (error) {
      console.error('❌ Error marking grammar point accessed:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Check if user has accessed a specific grammar point
  async hasUserAccessedGrammar(userId, level, chapter, grammarId, sessionType = 'Learn') {
    try {
      const genome = await this.getUserGrammarGenome(userId, level, chapter, sessionType);
      return genome[grammarId] === true;
    } catch (error) {
      console.error('❌ Error checking grammar access:', error);
      return false;
    }
  },

  // Get all accessed grammar points for a chapter
  async getAccessedGrammarPoints(userId, level, chapter, sessionType = 'Learn') {
    try {
      const genome = await this.getUserGrammarGenome(userId, level, chapter, sessionType);
      return Object.keys(genome).filter(key => genome[key] === true);
    } catch (error) {
      console.error('❌ Error getting accessed grammar points:', error);
      return [];
    }
  },

  // Initialize grammar genome for a chapter (mark all as false)
  async initializeGrammarGenome(userId, level, chapter, availableGrammarIds, sessionType = 'Learn') {
    try {
      console.group(`🧬 UserGrammarTracking: Initializing grammar genome`);
      console.log('User:', userId, 'Level:', level, 'Chapter:', chapter);
      console.log('Available grammar IDs:', availableGrammarIds);
      
      const genomePath = `users/${userId}/level_${level}/chapter_${chapter}/${sessionType}`;
      const genomeRef = doc(db, genomePath, 'grammar_genome');
      
      // Initialize all grammar points as not accessed
      const initialGenome = {};
      availableGrammarIds.forEach(grammarId => {
        initialGenome[grammarId] = false;
      });
      
      initialGenome.initialized_at = serverTimestamp();
      initialGenome.initialized_by = 'system';
      initialGenome.total_grammar_points = availableGrammarIds.length;
      
      await setDoc(genomeRef, initialGenome, { merge: true });
      
      console.log('✅ Grammar genome initialized:', initialGenome);
      console.groupEnd();
      
      return initialGenome;
    } catch (error) {
      console.error('❌ Error initializing grammar genome:', error);
      console.groupEnd();
      throw error;
    }
  },

  // Get grammar genome statistics
  async getGrammarGenomeStats(userId, level, chapter, sessionType = 'Learn') {
    try {
      const genome = await this.getUserGrammarGenome(userId, level, chapter, sessionType);
      const totalPoints = Object.keys(genome).filter(key => key !== 'last_updated' && key !== 'updated_by' && key !== 'initialized_at' && key !== 'initialized_by' && key !== 'total_grammar_points').length;
      const accessedPoints = Object.values(genome).filter(value => value === true).length;
      
      return {
        total_grammar_points: totalPoints,
        accessed_grammar_points: accessedPoints,
        remaining_grammar_points: totalPoints - accessedPoints,
        access_percentage: totalPoints > 0 ? (accessedPoints / totalPoints) * 100 : 0,
        genome: genome
      };
    } catch (error) {
      console.error('❌ Error getting grammar genome stats:', error);
      return {
        total_grammar_points: 0,
        accessed_grammar_points: 0,
        remaining_grammar_points: 0,
        access_percentage: 0,
        genome: {}
      };
    }
  }
};

export default UserGrammarTracking;
